import { RangeSliderInput } from './RangeSliderInput/RangeSliderInput';
import { isDomainLocale } from 'helpers/isDomainLocale';
import { useTypedTranslationFunction } from 'hooks/typescript/UseTypedTranslationFunction';
import { useDomainConfig } from 'hooks/useDomainConfig';
import {
    ChangeEventHandler,
    FocusEventHandler,
    KeyboardEventHandler,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { twJoin } from 'tailwind-merge';

type RangeSliderProps = {
    min: number;
    max: number;
    delay?: number;
    minValue: number;
    maxValue: number;
    setMinValueCallback: (value: number) => void;
    setMaxValueCallback: (value: number) => void;
    isDisabled?: boolean;
};

const DELIMITER_REGEXP = /[,.]/;

const getDecimalsLength = (value: number) => (value.toString().split(DELIMITER_REGEXP)[1] ?? '').length;

const getStep = (min: number, max: number): number => {
    const decimals = Math.max(getDecimalsLength(min), getDecimalsLength(max));

    return decimals === 0 ? 1 : Math.pow(10, -decimals);
};

const TEST_IDENTIFIER = 'basic-rangeslider';

const RangeSlider: FC<RangeSliderProps> = ({
    min,
    max,
    delay = 300,
    minValue,
    maxValue,
    setMinValueCallback,
    setMaxValueCallback,
    isDisabled,
}) => {
    const t = useTypedTranslationFunction();
    const step = getStep(min, max);

    const [minValueInput, setMinValueInput] = useState(min);
    const [minValueThumb, setMinValueThumb] = useState(min);

    const [maxValueInput, setMaxValueInput] = useState(max);
    const [maxValueThumb, setMaxValueThumb] = useState(max);

    const range = useRef<HTMLDivElement>(null);
    const { defaultLocale } = useDomainConfig();
    const isHungarianDomain = isDomainLocale(defaultLocale, 'hu');

    useEffect(() => {
        const timer = setTimeout(() => {
            if (minValueThumb !== minValue) {
                setMinValueCallback(minValueThumb);
            }
        }, delay);

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [minValueThumb, delay]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (maxValueThumb !== maxValue) {
                setMaxValueCallback(maxValueThumb);
            }
        }, delay);

        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [maxValueThumb, delay]);

    useEffect(() => {
        if (minValue < min) {
            setMinValueThumb(min);
            setMinValueInput(min);
        } else if (minValue > maxValue) {
            setMinValueThumb(maxValue);
            setMinValueInput(maxValue);
        } else {
            setMinValueThumb(minValue);
            setMinValueInput(minValue);
        }
    }, [maxValue, minValue, min]);

    useEffect(() => {
        if (maxValue > max) {
            setMaxValueThumb(max);
            setMaxValueInput(max);
        } else if (maxValue < minValue) {
            setMaxValueThumb(minValue);
            setMaxValueInput(minValue);
        } else {
            setMaxValueThumb(maxValue);
            setMaxValueInput(maxValue);
        }
    }, [maxValue, minValue, max]);

    const getPercent = useCallback((value: number) => Math.round(((value - min) / (max - min)) * 100), [min, max]);

    const onBlurMinHandler = useCallback<FocusEventHandler<HTMLInputElement>>(
        (event) => {
            const value = parseFloat(event.currentTarget.value);
            if (value < min || Number.isNaN(value)) {
                setMinValueThumb(min);
                setMinValueInput(min);
            } else {
                setMinValueCallback(value);
            }
        },
        [min, setMinValueCallback],
    );

    const onBlurMaxHandler = useCallback<FocusEventHandler<HTMLInputElement>>(
        (event) => {
            const value = parseFloat(event.currentTarget.value);
            if (value > max || Number.isNaN(value)) {
                setMaxValueThumb(max);
                setMaxValueInput(max);
            } else {
                setMaxValueCallback(value);
            }
        },
        [max, setMaxValueCallback],
    );

    const onChangeMaxInputHandler = useCallback<ChangeEventHandler<HTMLInputElement>>(
        (event) => setMaxValueInput(parseFloat(event.currentTarget.value)),
        [],
    );

    const onChangeMinInputHandler = useCallback<ChangeEventHandler<HTMLInputElement>>(
        (event) => setMinValueInput(parseFloat(event.currentTarget.value)),
        [],
    );

    const onKeyPressHandler = useCallback<KeyboardEventHandler<HTMLInputElement>>(
        (event) => event.key === 'Enter' && event.currentTarget.blur(),
        [],
    );

    const onChangeMinHandler = useCallback<ChangeEventHandler<HTMLInputElement>>(
        (event) => {
            const value = Math.min(Number(event.target.value), maxValueThumb);
            setMinValueThumb(value);
            setMinValueInput(value);
        },
        [maxValueThumb],
    );

    const onChangeMaxHandler = useCallback<ChangeEventHandler<HTMLInputElement>>(
        (event) => {
            const value = Math.max(Number(event.target.value), minValueThumb);
            setMaxValueThumb(value);
            setMaxValueInput(value);
        },
        [minValueThumb],
    );

    useEffect(() => {
        if (range.current) {
            const minPercent = getPercent(minValueThumb);
            const maxPercent = getPercent(maxValueThumb);

            range.current.style.left = `${minPercent}%`;
            range.current.style.width = `${maxPercent - minPercent}%`;
        }
    }, [getPercent, maxValueThumb, minValueThumb]);

    return (
        <>
            <div className="flex flex-row items-center justify-start">
                <div
                    className="mr-2 inline-block text-footer font-bold uppercase"
                    data-testid={TEST_IDENTIFIER + '-left-value'}
                >
                    {!isHungarianDomain && t('from')}
                    <RangeSliderInput
                        ariaLabel={t('Minimum price')}
                        value={minValueInput}
                        onChange={onChangeMinInputHandler}
                        onBlurCapture={onBlurMinHandler}
                        onKeyPress={onKeyPressHandler}
                        className={isHungarianDomain ? 'ml-0' : ''}
                        isDisabled={isDisabled}
                    />
                </div>
                <div
                    className="mr-2 inline-block text-footer font-bold uppercase"
                    data-testid={TEST_IDENTIFIER + '-right-value'}
                >
                    {!isHungarianDomain ? t('to') : '\u002D'}
                    <RangeSliderInput
                        ariaLabel={t('Maximum price')}
                        value={maxValueInput}
                        onChange={onChangeMaxInputHandler}
                        onBlurCapture={onBlurMaxHandler}
                        onKeyPress={onKeyPressHandler}
                        isDisabled={isDisabled}
                    />
                </div>
            </div>
            <div className="relative flex h-12 w-full items-center justify-center p-2" data-testid={TEST_IDENTIFIER}>
                <label htmlFor="minimalPriceSliderControl" className="sr-only">
                    {t('Set minimum price filter')}
                </label>
                <input
                    id="minimalPriceSliderControl"
                    type="range"
                    disabled={isDisabled}
                    min={min}
                    max={max}
                    step={step}
                    value={minValueThumb}
                    onChange={onChangeMinHandler}
                    data-testid={TEST_IDENTIFIER + '-left-thumb'}
                    className={twJoin('slider-thumb', (maxValueThumb === min || isDisabled) && 'slider-thumb-disabled')}
                />
                <label htmlFor="maximalPriceSliderControl" className="sr-only">
                    {t('Set maximum price filter')}
                </label>
                <input
                    id="maximalPriceSliderControl"
                    disabled={isDisabled}
                    type="range"
                    min={min}
                    max={max}
                    step={step}
                    value={maxValueThumb}
                    onChange={onChangeMaxHandler}
                    data-testid={TEST_IDENTIFIER + '-right-thumb'}
                    className={twJoin('slider-thumb', (maxValueThumb === max || isDisabled) && 'slider-thumb-disabled')}
                />
                <div className="relative w-full">
                    <div className="absolute z-above h-1 w-full rounded-sm bg-primary bg-opacity-20" />
                    <div
                        className={twJoin('absolute z-two h-1 rounded-sm bg-green', isDisabled && 'bg-grayMidDarker')}
                        ref={range}
                    />
                </div>
            </div>
        </>
    );
};

export default RangeSlider;
